import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
	FormControl,
	FormHelperText,
	Grid,
	IconButton,
	InputAdornment,
	InputLabel,
	OutlinedInput,
	Paper,
	styled,
} from "@mui/material";
import { StyledButton } from "components/StyledButton";
import { passwordAcceptable, passwordsMatch } from "helpers/authValidation";
import { PASSWORD_NO_MATCH, PASSWORD_POLICY_MSG } from "messages";
import { useState } from "react";
import { Link } from "react-router-dom";
import { getPath, routes } from "routes";

const PREFIX = "ChangePasswordPageForm";

const classes = {
	container: `${PREFIX}-container`,
	form: `${PREFIX}-form`,
	baseButton: `${PREFIX}-baseButton`,
	textField: `${PREFIX}-textField`,
	inputLabel: `${PREFIX}-inputLabel`,
};

const Root = styled("form")(({ theme }) => ({
	[`& .${classes.container}`]: {
		flexGrow: 1,
		justifyContent: "flex-start",
		padding: theme.spacing(2),
	},

	[`&.${classes.form}`]: {
		flexGrow: 1,
		"& .MuiTextField-root": {
			margin: theme.spacing(1),
		},
	},

	[`& .${classes.baseButton}`]: {
		padding: "10px 25px",
		float: "left",
		margin: "0 0 0 20px",
	},

	[`& .${classes.textField}`]: {
		paddingBottom: "1em",
	},

	[`& .${classes.inputLabel}`]: {
		"&": {
			backgroundColor: theme.palette.background.paper,
		},
	},
}));

export interface ChangePasswordPageFormProps {
	userIdParam?: string;
	password: string;
	setPassword: (password: string) => void;
	confirmPassword: string;
	setConfirmPassword: (password: string) => void;
	onSubmit: (event: React.FormEvent) => void;
}

export const ChangePasswordPageForm = ({
	userIdParam,
	password,
	setPassword,
	confirmPassword,
	setConfirmPassword,
	onSubmit,
}: ChangePasswordPageFormProps): JSX.Element => {
	const [showPass, setShowPass] = useState(false);
	const [showConfirmPass, setShowConfirmPass] = useState(false);

	return (
		<Root className={classes.form} onSubmit={onSubmit}>
			<Paper>
				<Grid
					container
					justifyContent="flex-start"
					direction="column"
					className={classes.container}
				>
					<FormHelperText>{PASSWORD_POLICY_MSG}</FormHelperText>
					<br />
					<FormControl variant="filled" className={classes.textField}>
						<InputLabel
							className={classes.inputLabel}
							htmlFor="outlined-adornment-password"
						>
							New password
						</InputLabel>
						<OutlinedInput
							required
							id="outlined-adornment-password-1"
							type={showPass ? "text" : "password"}
							fullWidth
							error={!passwordAcceptable(password) && password.length > 0}
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							inputProps={{ "data-testid": "new-password" }}
							endAdornment={
								<InputAdornment position="end">
									<IconButton
										aria-label="toggle password visibility"
										onClick={() => setShowPass(!showPass)}
										edge="end"
										data-testid="toggle-password-visibility-1"
										size="large"
									>
										{showPass ? <Visibility /> : <VisibilityOff />}
									</IconButton>
								</InputAdornment>
							}
						/>
					</FormControl>
					<FormControl variant="outlined" className={classes.textField}>
						<InputLabel
							className={classes.inputLabel}
							htmlFor="outlined-adornment-password"
						>
							Re-type new password
						</InputLabel>
						<OutlinedInput
							required
							id="outlined-adornment-password-2"
							type={showConfirmPass ? "text" : "password"}
							fullWidth
							value={confirmPassword}
							error={
								!passwordsMatch(password, confirmPassword) &&
								confirmPassword.length > 0
							}
							inputProps={{ "data-testid": "reconfirm-password" }}
							onChange={(e) => setConfirmPassword(e.target.value)}
							endAdornment={
								<InputAdornment position="end">
									<IconButton
										aria-label="toggle password visibility"
										onClick={() => setShowConfirmPass(!showConfirmPass)}
										edge="end"
										data-testid="toggle-password-visibility-2"
										size="large"
									>
										{showConfirmPass ? <Visibility /> : <VisibilityOff />}
									</IconButton>
								</InputAdornment>
							}
						/>
						<FormHelperText>
							{!passwordsMatch(password, confirmPassword) &&
							confirmPassword.length > 0
								? PASSWORD_NO_MATCH
								: ""}
						</FormHelperText>
					</FormControl>
					<Grid container justifyContent="flex-end" alignItems="center">
						<Grid item>
							<StyledButton
								variant="contained"
								className={classes.baseButton}
								component={Link}
								to={
									userIdParam
										? getPath(routes._.people._.userProfile, {
												userId: userIdParam,
										  })
										: routes._.people._.me.path
								}
								data-testid="cancel-password-change"
							>
								Cancel
							</StyledButton>
						</Grid>
						<Grid item>
							<StyledButton
								variant="contained"
								color="primary"
								className={classes.baseButton}
								disabled={!passwordsMatch(password, confirmPassword)}
								type="submit"
								data-testid="submit-password-change"
							>
								Change password
							</StyledButton>
						</Grid>
					</Grid>
				</Grid>
			</Paper>
		</Root>
	);
};
